'use client';

import React from 'react';
import { TasticProps } from '../types';
import { TemplatesComponents } from 'components/genius-ui/atoms/templateLayout/templateComponents';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, smallMobile } from 'helpers/utils/screensizes';
import { excludedModules, MAGAZINEINTROPDP, ModuleName } from 'helpers/utils/constantStrings';
import { EXCLUDED_PRODUCT_MODULES } from 'helpers/utils/constantStrings';
import { BENNER_IMAGE_MODULES } from 'helpers/utils/constantStrings';
import NotFoundPage from 'components/genius-ui/organisms/404';
import { usePathname } from 'next/navigation';

interface TemplatePageModules {
  id: string;
  entityId?: string;
  key: string;
  width: number;
  publish?: boolean;
  date?: string;
  moduleName?: string[];
}
interface TemplatePageColumns {
  modules: TemplatePageModules[];
  width: number;
}
interface TemplatePageRows {
  publish?: boolean;
  date?: string;
  columns: TemplatePageColumns[];
}
export interface TemplatePageProps {
  modulesOrder: {
    rows: TemplatePageRows[];
  };
  staticContentCollection?: {
    items: StaticContentCollectionItems[];
  };
}

interface StaticContentCollectionItems {
  components?: any;
  headline?: string;
  headlineType?: string;
  moduleName?: string[];
}
export interface TemplateLayoutProps {
  data: {
    dataSource: {
      page?: TemplatePageProps;
      [key: string]: any;
    };
  };
}

const TemplateLayoutTastic = ({ data }: TasticProps<TemplateLayoutProps>) => {
  const currentDate = new Date().getTime();
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMobileSize] = useMediaQuery(smallMobile);
  const pathname = usePathname();
  let validIndex = 0;
  let currentIndex: number | null = 0;
  let bonusOrUpsellModuleIndex: number | null = null;

  if (
    data?.data?.dataSource?.notFound === true ||
    pathname === '/404/' ||
    data?.data?.dataSource?.ERROR === 'NO SLUG'
  ) {
    return <NotFoundPage />;
  }

  return (
    <>
      <div>
        {data?.data?.dataSource?.page?.modulesOrder?.rows
          ?.filter((row) => {
            const rowPublish = row?.publish ?? true;
            const rowDate = new Date(row?.date || 0).getTime();
            return rowPublish || (!rowPublish && rowDate < currentDate);
          })
          ?.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: 'flex',
                flexDirection:
                  isMobileSize && !isDesktopSize
                    ? row?.columns?.some((column) => column?.width < 100)
                      ? 'column'
                      : 'row'
                    : 'row',
                outline: 'none',
              }}
            >
              {row?.columns?.map((column, columnIndex) => (
                <div
                  key={columnIndex}
                  style={{
                    width: `${isDesktopSize ? column?.width : 100}%`,
                    display: 'flex',
                    flexWrap: 'wrap',
                  }}
                >
                  {column?.modules
                    ?.filter((module) => {
                      const moduleId = module?.id || module?.entityId;
                      const modulePublish = module?.publish ?? true;
                      const moduleDate = new Date(module?.date || 0).getTime();
                      const moduleCollectionData =
                        data?.data?.dataSource[module?.key] || data?.data?.dataSource[module?.key + '||' + moduleId];
                      return (
                        (modulePublish || (!modulePublish && moduleDate < currentDate)) &&
                        moduleCollectionData !== null &&
                        moduleCollectionData !== undefined &&
                        !(Array.isArray(moduleCollectionData) && moduleCollectionData.length === 0)
                      );
                    })
                    ?.map((module, moduleIndex) => {
                      const Component = TemplatesComponents[module?.key];
                      if (Component) {
                        const moduleId = module?.id || module?.entityId;
                        const moduleCollectionData =
                          data?.data?.dataSource[module?.key] || data?.data?.dataSource[module?.key + '||' + moduleId];
                        const pdpData = data?.data?.dataSource?.pdpData;

                        const modulePlaceholder =
                          moduleCollectionData?.modulePlaceholder || moduleCollectionData?.[0]?.modulePlaceholder;
                        const currentModuleName =
                          modulePlaceholder?.moduleName ||
                          moduleCollectionData?.moduleName ||
                          moduleCollectionData?.moduleData?.modulePlaceholder?.moduleName;

                        const productData = moduleCollectionData?.productData;
                        const bonusProduct = productData?.bonusUpsellSkuList?.bonus?.length > 0;
                        const upsellProduct = productData?.bonusUpsellSkuList?.upsell?.length > 0;
                        const magazineArticleCollection =
                          currentModuleName?.includes(ModuleName?.MagazineArticleListing) &&
                          !moduleCollectionData?.data?.magazineArticleCollection;

                        const magazineIntroPdp =
                          currentModuleName?.includes(MAGAZINEINTROPDP?.MagazineIntroPDP) &&
                          moduleCollectionData?.magazineArticles?.length === 0;

                        const magazineSingleArticle = currentModuleName?.includes(ModuleName?.MagazineSingleArticle);
                        const bannerImage =
                          currentModuleName?.some((name: string) => BENNER_IMAGE_MODULES.includes(name)) &&
                          moduleCollectionData?.wrapperImageCollection?.items?.length === 0;
                        const isExcludedModule = currentModuleName?.some((name: string) => excludedModules.has(name));
                        const isBonusOrUpsellModule = currentModuleName?.some((name: string) =>
                          EXCLUDED_PRODUCT_MODULES.includes(name),
                        );
                        const isExcludeBackgroundCategoryComponent =
                          currentModuleName?.includes(ModuleName?.categoryHeadline) &&
                          moduleCollectionData?.modulePlaceholder?.excludeBackground === true;

                        const isExcluded =
                          isExcludedModule ||
                          (isBonusOrUpsellModule && !bonusProduct && !upsellProduct) ||
                          bannerImage ||
                          magazineArticleCollection ||
                          magazineIntroPdp ||
                          magazineSingleArticle ||
                          isExcludeBackgroundCategoryComponent;
                        if (!isExcluded) {
                          currentIndex = validIndex;

                          if (
                            (module?.key === ModuleName?.TvUpsellProducts && upsellProduct) ||
                            (module?.key === ModuleName?.BonusProducts && bonusProduct)
                          ) {
                            if (bonusOrUpsellModuleIndex === null) {
                              bonusOrUpsellModuleIndex = validIndex;
                            }
                            currentIndex = bonusOrUpsellModuleIndex;
                          }

                          if (
                            module?.key !== ModuleName?.TvUpsellProducts &&
                            module?.key !== ModuleName?.BonusProducts
                          ) {
                            validIndex++;
                          } else if (bonusOrUpsellModuleIndex === validIndex) {
                            validIndex++;
                          }
                        } else {
                          currentIndex = null;
                        }

                        if (moduleCollectionData?.productData === null && !!pdpData) {
                          moduleCollectionData.productData = pdpData;
                        }

                        return (
                          <div key={moduleIndex} style={{ width: `${module?.width || 100}%` }}>
                            <div className="w-full">
                              <Component dataProps={moduleCollectionData} indexing={currentIndex} />
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                </div>
              ))}
            </div>
          ))}
      </div>
    </>
  );
};

export default TemplateLayoutTastic;
